"use client";

import { useCallback, useMemo } from "react";
import { X } from "lucide-react";
import type { FilterConfig } from "@/lib/types/data.types";
import type { FilterColumnDef } from "./types";
import { getColumnDef, getOperatorsForColumn } from "./helpers";
import { FilterValueInput } from "./FilterValueInput";
import { Select } from "@/components/forms";

interface FilterRowProps {
  columns: FilterColumnDef[];
  filter: FilterConfig;
  onChange: (updates: Partial<FilterConfig>) => void;
  onRemove: () => void;
}

export function FilterRow({
  columns,
  filter,
  onChange,
  onRemove,
}: FilterRowProps) {
  const columnDef = useMemo(
    () => getColumnDef(columns, filter.column),
    [columns, filter.column]
  );

  const operators = useMemo(
    () => (columnDef ? getOperatorsForColumn(columnDef) : []),
    [columnDef]
  );

  const hideOperator = operators.length <= 1;
  const isEmpty = !filter.value;

  const handleColumnChange = useCallback(
    (key: string) => {
      const col = columns.find((c) => c.key === key);
      const ops = col ? getOperatorsForColumn(col) : [];
      onChange({
        column: key,
        operator: ops[0]?.value ?? "",
        value: "",
      });
    },
    [columns, onChange]
  );

  const isMultiSelect = columnDef?.type === "multi-select";

  const selectedMultiValues = useMemo(
    () =>
      isMultiSelect && filter.value
        ? String(filter.value).split(",").map((v) => v.trim()).filter(Boolean)
        : [],
    [isMultiSelect, filter.value]
  );

  const handleValueChange = useCallback(
    (value: string) => {
      onChange({ value });
    },
    [onChange]
  );

  const handleMultiValueChange = useCallback(
    (values: string[]) => {
      onChange({ value: values.join(",") });
    },
    [onChange]
  );

  return (
    <div className="flex items-center gap-2">
      {/* Column select */}
      <div className="flex-1 min-w-0">
        <Select
          selectSize="xs"
          value={filter.column}
          onChange={(value) => handleColumnChange(value as string)}
          options={columns.map((col) => ({
            value: col.key,
            label: col.label,
          }))}
        />
      </div>

      {/* Operator select (hidden when single operator) */}
      {!hideOperator && (
        <div className="flex-1 min-w-0">
          <Select
            selectSize="xs"
            value={filter.operator}
            onChange={(value) => onChange({ operator: value as string })}
            options={operators.map((op) => ({
              value: op.value,
              label: op.label,
            }))}
          />
        </div>
      )}

      {/* Value input */}
      <div className="flex-1 min-w-0">
        {columnDef ? (
          <FilterValueInput
            columnDef={columnDef}
            value={filter.value?.toString() ?? ""}
            onChange={handleValueChange}
            selectedValues={selectedMultiValues}
            onSelectedValuesChange={handleMultiValueChange}
            compact
            isEmpty={isEmpty}
          />
        ) : (
          <input
            type="text"
            disabled
            value=""
            placeholder="Select column"
            className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-400 focus:outline-none"
          />
        )}
      </div>

      {/* Remove button */}
      <button
        type="button"
        onClick={onRemove}
        className="p-1.5 rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
        title="Remove filter"
      >
        <X className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
      </button>
    </div>
  );
}
